// Una ubicación puede tener partes en distintos lugares del plano (por ejemplo
// mesas de "Mesas" acá y otras allá): mismas tarifas y mismo cupo. En el
// editor cada parte es un sector propio con editor.parteDe = clave de la
// ubicación; al guardar se juntan en ella (editor.partes guarda dónde está
// cada parte y qué lugares tiene) y al abrir se vuelven a separar.
import { conLugares, nombreUnico, nuevaClave } from "./mapa";

const girar = (x, y, grados) => {
  const r = (grados * Math.PI) / 180;
  return { x: x * Math.cos(r) - y * Math.sin(r), y: x * Math.sin(r) + y * Math.cos(r) };
};

// llevar: un punto de las coordenadas de un sector a las de otro
export const llevar = (desde, hacia, x, y) => {
  const p = girar(x, y, desde.rotacion || 0);
  return girar(desde.x + p.x - hacia.x, desde.y + p.y - hacia.y, -(hacia.rotacion || 0));
};

const redondo = (p) => ({ x: Math.round(p.x), y: Math.round(p.y) });

export const llevarMesa = (mesa, desde, hacia) => {
  const giro = (desde.rotacion || 0) - (hacia.rotacion || 0);
  return { ...mesa, ...redondo(llevar(desde, hacia, mesa.x, mesa.y)), rotacion: (mesa.rotacion || 0) + giro };
};

export const llevarFila = (fila, desde, hacia) => {
  const giro = (desde.rotacion || 0) - (hacia.rotacion || 0);
  return {
    ...fila,
    ...redondo(llevar(desde, hacia, fila.x || 0, fila.y || 0)),
    asientos: fila.asientos.map((a) => ({ ...a, ...redondo(llevar(desde, hacia, a.x, a.y)), rotacion: Math.round((a.rotacion || 0) + giro) })),
  };
};

export const esParte = (sector) => Boolean(sector?.editor?.parteDe);

// ubicacionDe: la clave de la ubicación real (la principal, si es una parte)
export const ubicacionDe = (sector) => sector?.editor?.parteDe || sector?.clave;

export const partesDe = (mapa, clave) => mapa.sectores.filter((s) => s.editor?.parteDe === clave);

// nombresUsados: nombres de mesas y filas de toda la ubicación (deben ser únicos)
export const nombresUsados = (mapa, clave, excepto = null) => {
  const usados = new Set();
  mapa.sectores
    .filter((s) => ubicacionDe(s) === clave && s.clave !== excepto)
    .forEach((s) => {
      s.mesas.forEach((m) => usados.add(m.nombre.toLowerCase()));
      s.filas.forEach((f) => f.nombre && usados.add(f.nombre.toLowerCase()));
    });
  return usados;
};

export const nombreLibre = (usados, nombre) => {
  if (!usados.has(nombre.toLowerCase())) return nombre;
  for (let n = 2; ; n++) if (!usados.has(`${nombre} (${n})`.toLowerCase())) return `${nombre} (${n})`;
};

const renombrar = (sector, usados) => {
  const mesas = sector.mesas.map((m) => {
    const nombre = nombreLibre(usados, m.nombre);
    usados.add(nombre.toLowerCase());
    return nombre === m.nombre ? m : { ...m, nombre };
  });
  const filas = sector.filas.map((f) => {
    if (!f.nombre) return f;
    const nombre = nombreLibre(usados, f.nombre);
    usados.add(nombre.toLowerCase());
    return nombre === f.nombre ? f : { ...f, nombre };
  });
  return { ...sector, mesas, filas };
};

// hacerParte convierte el sector en una parte de la ubicación (renombrando lo
// que choque). Sus tarifas pasan a ser las de la ubicación.
export const hacerParte = (mapa, claveSector, claveUbicacion) => {
  const principal = mapa.sectores.find((s) => s.clave === claveUbicacion);
  const sector = mapa.sectores.find((s) => s.clave === claveSector);
  if (!principal || !sector) return mapa;
  const { pieza, filasDeOrigen, ...editor } = sector.editor || {};
  const parte = {
    ...renombrar(sector, nombresUsados(mapa, claveUbicacion, claveSector)),
    editor: { ...editor, parteDe: claveUbicacion },
  };
  return sincronizarPartes({ ...mapa, sectores: mapa.sectores.map((s) => (s.clave === claveSector ? parte : s)) });
};

// separarParte: la parte pasa a ser una ubicación propia, sin tarifas
export const separarParte = (mapa, clave) => {
  const parte = mapa.sectores.find((s) => s.clave === clave);
  if (!esParte(parte)) return mapa;
  const { parteDe, ...editor } = parte.editor;
  const suelta = { ...parte, editor, tarifas: [], nombre: nombreUnico(mapa, `${parte.nombre} · aparte`) };
  return { ...mapa, sectores: mapa.sectores.map((s) => (s.clave === clave ? suelta : s)) };
};

// sincronizarPartes: cada parte copia nombre, tarifas y límite de su
// ubicación; una parte sin ubicación (o de una general, que se vende por
// cantidad) vuelve a ser una ubicación sin tarifas, y una parte vacía se
// borra. Devuelve el mismo mapa si no cambia nada.
export const sincronizarPartes = (mapa) => {
  const porClave = new Map(mapa.sectores.map((s) => [s.clave, s]));
  let cambio = false;
  const sectores = [];
  mapa.sectores.forEach((s) => {
    if (!esParte(s)) return sectores.push(s);
    const principal = porClave.get(s.editor.parteDe);
    if (!conLugares(s)) {
      cambio = true;
      return;
    }
    if (!principal || esParte(principal) || !conLugares(principal)) {
      const { parteDe, ...editor } = s.editor;
      cambio = true;
      // contra los nombres ya resueltos, por si se sueltan varias juntas
      const nombre = principal ? nombreUnico({ ...mapa, sectores: [...sectores, ...mapa.sectores] }, `${s.nombre} · aparte`) : s.nombre;
      return sectores.push({ ...s, editor, tarifas: [], nombre });
    }
    const igual =
      s.nombre === principal.nombre &&
      s.limite_persona === principal.limite_persona &&
      s.tarifas.length === principal.tarifas.length &&
      s.tarifas.every((t, i) => t === principal.tarifas[i]);
    if (igual) return sectores.push(s);
    cambio = true;
    sectores.push({ ...s, nombre: principal.nombre, limite_persona: principal.limite_persona, tarifas: principal.tarifas });
  });
  return cambio ? { ...mapa, sectores } : mapa;
};

// juntarPartes: para guardar, cada parte pasa a su ubicación
export const juntarPartes = (mapa) => {
  const partes = mapa.sectores.filter(esParte);
  if (partes.length === 0) return mapa;
  const sectores = mapa.sectores
    .filter((s) => !esParte(s))
    .map((principal) => {
      const propias = partes.filter((p) => p.editor.parteDe === principal.clave);
      if (propias.length === 0) return principal;
      const origen = { x: 0, y: 0 };
      const registro = propias.map((p) => {
        const { parteDe, ...editor } = p.editor;
        return {
          clave: p.clave,
          ...redondo(llevar(p, principal, origen.x, origen.y)),
          rotacion: (p.rotacion || 0) - (principal.rotacion || 0),
          ancho: p.ancho,
          alto: p.alto,
          forma: p.forma || [],
          editor,
          decorativos: p.decorativos,
          mesas: p.mesas.map((m) => m.nombre),
          filas: p.filas.map((f) => f.nombre),
        };
      });
      return {
        ...principal,
        mesas: [...principal.mesas, ...propias.flatMap((p) => p.mesas.map((m) => llevarMesa(m, p, principal)))],
        filas: [...principal.filas, ...propias.flatMap((p) => p.filas.map((f) => llevarFila(f, p, principal)))],
        editor: { ...principal.editor, partes: registro },
      };
    });
  return { ...mapa, sectores };
};

// separarPartes: al abrir, cada parte guardada vuelve a ser un sector propio
export const separarPartes = (mapa) => {
  if (!mapa.sectores.some((s) => s.editor?.partes?.length)) return mapa;
  const sectores = [];
  mapa.sectores.forEach((principal) => {
    const registro = principal.editor?.partes;
    if (!registro?.length) return sectores.push(principal);
    const { partes, ...editorPrincipal } = principal.editor;
    let mesas = principal.mesas;
    let filas = principal.filas;
    const nuevas = registro.map((r) => {
      const p0 = llevar({ x: principal.x, y: principal.y, rotacion: principal.rotacion }, { x: 0, y: 0, rotacion: 0 }, r.x, r.y);
      const parte = {
        ...principal,
        id: undefined,
        con_ventas: false,
        en_mapa: true,
        clave: r.clave || nuevaClave("s"),
        x: Math.round(p0.x),
        y: Math.round(p0.y),
        rotacion: (principal.rotacion || 0) + (r.rotacion || 0),
        ancho: r.ancho,
        alto: r.alto,
        forma: r.forma || [],
        decorativos: r.decorativos || [],
        editor: { ...(r.editor || {}), parteDe: principal.clave },
        mesas: [],
        filas: [],
      };
      const nombresMesas = new Set(r.mesas || []);
      const nombresFilas = new Set(r.filas || []);
      parte.mesas = mesas.filter((m) => nombresMesas.has(m.nombre)).map((m) => llevarMesa(m, principal, parte));
      parte.filas = filas.filter((f) => nombresFilas.has(f.nombre)).map((f) => llevarFila(f, principal, parte));
      mesas = mesas.filter((m) => !nombresMesas.has(m.nombre));
      filas = filas.filter((f) => !nombresFilas.has(f.nombre));
      return parte;
    });
    sectores.push({ ...principal, mesas, filas, editor: editorPrincipal }, ...nuevas);
  });
  return sincronizarPartes({ ...mapa, sectores });
};
